import { createApp } from 'vue';
import 'external-svg-loader';
import axios from 'axios';

import { $i18n } from '@/provider/plugin';
import { $settings } from '@/provider/settings';
import { eventBus } from '@/utils/eventBus';
import { registerPrimeVueComponents } from '@/plugins/primeVue';

// PrimeVue
import PrimeVue from 'primevue/config';
// eslint-disable-next-line import/no-extraneous-dependencies
import { definePreset } from '@primevue/themes';
// eslint-disable-next-line import/no-extraneous-dependencies
import Material from '@primevue/themes/material';
import wxpvConfig from '@/configs/PrimeVue/primeVueConfig';

import ToastService from 'primevue/toastservice';
import DialogService from 'primevue/dialogservice';
import ConfirmationService from 'primevue/confirmationservice';

import { createPinia } from 'pinia';
import VueDOMPurifyHtml from 'vue-dompurify-html';
import * as Sentry from '@sentry/vue';

import filters from '@/filters/filters';

import App from '@/App.vue';
import router from '@/router';

const pinia = createPinia();
const app = createApp(App);

const wxPreset = definePreset(Material, wxpvConfig);

app.use($i18n);
app.use(pinia);
app.use(ToastService);
app.use(ConfirmationService);
app.use(DialogService);
app.use(VueDOMPurifyHtml, {
  default: {
    USE_PROFILES: { html: true },
  },
});

app.use(PrimeVue, {
  theme: {
    preset: wxPreset,
  },
  options: {
    darkModeSelector: false,
    prefix: 'p',
    cssLayer: false,
  },
});

if (import.meta.env.MODE === 'production') {
  axios({
    url: [$settings.middlewareURI, 'sentry'].join('/'),
  }).then((response) => {
    const sentryDsn = response.data;

    Sentry.init({
      dsn: sentryDsn,
      // Data Scrubbing PII:
      // Called for message and error events
      beforeSend(event) {
        // Modify or drop the event here
        if (event.user) {
          // Don't send user's email address
          delete event.user.email;
        }
        return event;
      },
      integrations: [
        Sentry.browserTracingIntegration({
          // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
          tracePropagationTargets: ['localhost'],
          router,
        }),
      ],
      // Performance Monitoring
      tracesSampleRate: 1, //  Capture 100% of the transactions
      tracingOptions: {
        trackComponents: true,
        timeout: 500,
        hooks: ['activate', 'create', 'unmount', 'mount', 'update'],
      },
      environment: import.meta.env.MODE,
      release: `webXperience@${$settings.release}`,
    });
  });
}

// make all important packages available on the $application instance for js module imports besides components,
// since js modules don't have the $application context anymore as it was with Vue2
const registerObject = [
  ['$i18n', $i18n],
  ['$filters', filters],
  ['$eventBus', eventBus],
  ['$confirm', app.config.globalProperties.$confirm],
];

registerObject.forEach(([key, value]) => {
  if (!app.config.globalProperties[key])
    app.config.globalProperties[key] = value;
});

registerPrimeVueComponents(app);

app.directive('loading', (element, binding) => {
  const { loading, key, scale } = binding.value;
  const classSelector = key ? `loading--${key}` : 'loading';

  if (loading && !element.querySelector(`.${classSelector}`)) {
    const loadSpinner = document.createElement('div');
    loadSpinner.className = `loading ${key ? `loading--${key}` : ''} ${
      scale === 0.5 ? 'small' : ''
    } `;
    loadSpinner.innerHTML = '<div class="effect-1 effects" />';
    element.append(loadSpinner);
  } else if (
    loading === false &&
    element.querySelectorAll(`.${classSelector}`)
  ) {
    element.querySelectorAll(`.${classSelector}`).forEach((selectedElement) => {
      selectedElement.remove();
    });
  }
});

app.mixin({
  mounted() {
    this.$el.__vue__ = this; // provide component instances on elements
  },
});

export { app };
